import apiClient from '../utils/api';

/**
 * Stock Service Layer
 * Handles all stock-related API calls with proper error handling and logging
 */
class StockService {
    /**
     * Get current stock price and basic information
     * @param {string} symbol - Stock symbol (e.g., 'AAPL', 'MSFT')
     * @returns {Promise<Object>} Stock price data
     */
    static async getStockPrice(symbol) {
        try {
            console.log(`Stock Service: Fetching price for ${symbol}`);
            
            const response = await apiClient.get(`/api/stocks/${symbol}/price`);
            
            console.log(`Stock Service: Price data for ${symbol}`, response);
            return response;
            
        } catch (error) {
            console.error(`Stock Service Price Error for ${symbol}:`, error);
            throw new Error(`Failed to fetch stock price for ${symbol}: ${error.message}`);
        }
    }
    
    /**
     * Get historical stock data
     * @param {string} symbol - Stock symbol
     * @param {string} period - Time period ('1d', '5d', '1mo', '3mo', '6mo', '1y', '2y', '5y', '10y', 'ytd', 'max')
     * @returns {Promise<Object>} Historical stock data
     */
    static async getStockHistory(symbol, period = '1y') {
        try {
            console.log(`Stock Service: Fetching history for ${symbol} with period ${period}`);
            
            const response = await apiClient.get(`/api/stocks/${symbol}/history?period=${period}`);
            
            console.log(`Stock Service: History data for ${symbol}`, response);
            return response;
            
        } catch (error) {
            console.error(`Stock Service History Error for ${symbol}:`, error);
            throw new Error(`Failed to fetch stock history for ${symbol}: ${error.message}`);
        }
    }
    
    /**
     * Get comprehensive stock information
     * @param {string} symbol - Stock symbol
     * @returns {Promise<Object>} Company information
     */
    static async getStockInfo(symbol) {
        try {
            console.log(`Stock Service: Fetching info for ${symbol}`);
            
            const response = await apiClient.get(`/api/stocks/${symbol}/info`);
            
            console.log(`Stock Service: Info data for ${symbol}`, response);
            return response;
            
        } catch (error) {
            console.error(`Stock Service Info Error for ${symbol}:`, error);
            throw new Error(`Failed to fetch stock info for ${symbol}: ${error.message}`);
        }
    }
    
    /**
     * Get all stock data (price, history, info) in one call
     * @param {string} symbol - Stock symbol
     * @param {string} period - Time period for historical data
     * @returns {Promise<Object>} Complete stock data
     */
    static async getAllStockData(symbol, period = '1y') {
        try {
            console.log(`Stock Service: Fetching all data for ${symbol} with period ${period}`);
            
            const response = await apiClient.get(`/api/stocks/${symbol}/all?period=${period}`);
            
            console.log(`Stock Service: Complete data for ${symbol}`, response);
            return response;
            
        } catch (error) {
            console.error(`Stock Service All Data Error for ${symbol}:`, error);
            throw new Error(`Failed to fetch complete stock data for ${symbol}: ${error.message}`);
        }
    }
    
    /**
     * Get price data for multiple stocks in one call
     * @param {Array<string>} symbols - Array of stock symbols
     * @returns {Promise<Object>} Price data for all stocks
     */
    static async getMultipleStocksData(symbols) {
        try {
            console.log(`Stock Service: Fetching data for multiple stocks: ${symbols}`);
            
            const response = await apiClient.post('/api/stocks/batch', symbols);
            
            console.log(`Stock Service: Batch data for ${symbols.length} stocks`, response);
            return response;
            
        } catch (error) {
            console.error('Stock Service Batch Error:', error);
            throw new Error(`Failed to fetch batch stock data: ${error.message}`);
        }
    }
    
    /**
     * Search for stocks by name or symbol
     * @param {string} query - Search query
     * @returns {Promise<Object>} Search results
     */
    static async searchStocks(query) {
        try {
            console.log(`Stock Service: Searching stocks with query: ${query}`);
            
            const response = await apiClient.get(`/api/stocks/search/${query}`);
            
            console.log(`Stock Service: Search results for ${query}`, response);
            return response;
            
        } catch (error) {
            console.error(`Stock Service Search Error for ${query}:`, error);
            throw new Error(`Failed to search stocks for ${query}: ${error.message}`);
        }
    }
    
    /**
     * Get stock data with error handling and fallback
     * @param {string} symbol - Stock symbol
     * @param {string} dataType - Type of data to fetch ('price', 'history', 'info', 'all')
     * @param {Object} options - Additional options
     * @returns {Promise<Object>} Stock data with error information
     */
    static async getStockDataWithFallback(symbol, dataType = 'price', options = {}) {
        try {
            console.log(`Stock Service: Fetching ${dataType} data for ${symbol} with fallback`);
            
            let data;
            switch (dataType) {
                case 'price':
                    data = await this.getStockPrice(symbol);
                    break;
                case 'history':
                    data = await this.getStockHistory(symbol, options.period);
                    break;
                case 'info':
                    data = await this.getStockInfo(symbol);
                    break;
                case 'all':
                    data = await this.getAllStockData(symbol, options.period);
                    break;
                default:
                    throw new Error(`Unknown data type: ${dataType}`);
            }
            
            return {
                success: true,
                data: data,
                symbol: symbol,
                dataType: dataType,
                timestamp: new Date().toISOString()
            };
            
        } catch (error) {
            console.error(`Stock Service Fallback Error for ${symbol}:`, error);
            return {
                success: false,
                error: error.message,
                symbol: symbol,
                dataType: dataType,
                timestamp: new Date().toISOString()
            };
        }
    }
    
    /**
     * Get stock data with retry logic
     * @param {string} symbol - Stock symbol
     * @param {string} dataType - Type of data to fetch
     * @param {number} maxRetries - Maximum number of retries
     * @param {number} delay - Delay between retries in milliseconds
     * @returns {Promise<Object>} Stock data
     */
    static async getStockDataWithRetry(symbol, dataType = 'price', maxRetries = 3, delay = 1000) {
        let lastError;
        
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                console.log(`Stock Service: Attempt ${attempt}/${maxRetries} for ${symbol} ${dataType}`);
                
                const result = await this.getStockDataWithFallback(symbol, dataType);
                
                if (result.success) {
                    console.log(`Stock Service: Success on attempt ${attempt} for ${symbol}`);
                    return result;
                } else {
                    lastError = result.error;
                }
                
            } catch (error) {
                lastError = error.message;
                console.error(`Stock Service: Attempt ${attempt} failed for ${symbol}:`, error);
            }
            
            if (attempt < maxRetries) {
                console.log(`Stock Service: Waiting ${delay}ms before retry for ${symbol}`);
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
        
        throw new Error(`Failed to fetch ${dataType} data for ${symbol} after ${maxRetries} attempts: ${lastError}`);
    }
}

export default StockService; 